import { cn } from '../../lib/cn';
import { Card } from './Card';
import { Badge } from './Badge';
import { Button } from './Button';
import { ReasoningPanel } from './ReasoningPanel';
import type { AIXRecommendation, ApprovalAction } from '../../aix/framework';
import { APPROVAL_ACTIONS, categoryLabel, roleLabel } from '../../aix/framework';

type Props = {
  recommendation: AIXRecommendation;
  onAction?: (action: ApprovalAction) => void;
  className?: string;
};

const PRIMARY_ACTIONS: ApprovalAction[] = ['approve', 'reject', 'modify', 'simulate_again'];
const SECONDARY_ACTIONS: ApprovalAction[] = ['compare', 'escalate', 'delegate', 'schedule'];

export function ApprovalCard({ recommendation, onAction, className }: Props) {
  const { explanation, reasoning, title, category, role, source_module, automation_level } = recommendation;

  const actionLabel = (id: ApprovalAction) =>
    APPROVAL_ACTIONS.find((a) => a.id === id)?.label ?? id;

  return (
    <Card className={cn('border-l-4 border-l-ai-500', className)}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="ai">Approval Required</Badge>
            <Badge variant="default">{categoryLabel(category)}</Badge>
            {role && <span className="text-xs text-slate-500">{roleLabel(role)}</span>}
            {source_module && (
              <span className="text-xs text-slate-400">via {source_module}</span>
            )}
          </div>
          <h3 className="mt-2 font-medium text-slate-900 dark:text-slate-100">{title}</h3>
        </div>
        <span className="text-xs text-slate-400">L{automation_level}</span>
      </div>

      <dl className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-xs font-medium text-slate-500">What</dt>
          <dd className="text-slate-700 dark:text-slate-300">{explanation.what}</dd>
        </div>
        <div>
          <dt className="text-xs font-medium text-slate-500">Why</dt>
          <dd className="text-slate-700 dark:text-slate-300">{explanation.why}</dd>
        </div>
        {explanation.expected_impact && (
          <div>
            <dt className="text-xs font-medium text-slate-500">Expected Impact</dt>
            <dd className="text-slate-700 dark:text-slate-300">{explanation.expected_impact}</dd>
          </div>
        )}
        {explanation.estimated_effort && (
          <div>
            <dt className="text-xs font-medium text-slate-500">Effort</dt>
            <dd className="text-slate-700 dark:text-slate-300">{explanation.estimated_effort}</dd>
          </div>
        )}
      </dl>

      {explanation.evidence && explanation.evidence.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-medium text-slate-500">Evidence</p>
          <ul className="mt-1 text-xs text-slate-600 dark:text-slate-400">
            {explanation.evidence.map((e) => <li key={e}>• {e}</li>)}
          </ul>
        </div>
      )}

      <div className="mt-4">
        <ReasoningPanel reasoning={reasoning} confidence={explanation.confidence} defaultExpanded={false} />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {PRIMARY_ACTIONS.map((action) => (
          <Button
            key={action}
            variant={action === 'approve' ? 'primary' : action === 'reject' ? 'destructive' : 'secondary'}
            size="sm"
            onClick={() => onAction?.(action)}
          >
            {actionLabel(action)}
          </Button>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {SECONDARY_ACTIONS.map((action) => (
          <Button key={action} variant="ghost" size="sm" onClick={() => onAction?.(action)}>
            {actionLabel(action)}
          </Button>
        ))}
      </div>
    </Card>
  );
}
